import { MouseEventHandler, ReactNode, useCallback, useMemo } from "react";
import { TableColumn } from "@components/Table/TableColumns";
import { getFormattedThousand, getRem, markMatch, Tooltip } from "@datassential/platform-ui-lib";
import { CorrelationItem } from "@api/useCorrelation";
import HorizontalBar from "@components/HorizontalBarChart/HorizontalBar";
import { Metric } from "@models/Metric";
import cn from "classnames";
import { getStringContentFromReactNode } from "@core/utils/string";
import { useSelectedKeywordsContext } from "@vms/SelectedKeywords/SelectedKeywordsContext";
import TableCellSelectKeyword from "@vms/TableCells/TableCellSelect";

export const NAME_COL_WIDTH = 300;
export const NUM_COL_WIDTH = 120;

export type CorrelationTableConfig = {
    data: CorrelationItem[],
    search: string,
    selectedCorrelatingMetric: Metric | null,
    selectedHeadline: ReactNode,
    relatingHeadline: string,
}

export function useCorrelationTableConfig({
    data,
    search,
    selectedCorrelatingMetric,
    selectedHeadline,
    relatingHeadline,
}: CorrelationTableConfig) {
    const {
        selectedKeywordsIds,
    } = useSelectedKeywordsContext();
    const trimmedSearch = useMemo(() => search.trim(), [search])
    const hideSorting = useMemo(() => trimmedSearch.length > 0, [trimmedSearch])

    const hasMaxSelected = useMemo(
        () => selectedKeywordsIds.length >= 25,
        [selectedKeywordsIds]
    );

    const preventClick: MouseEventHandler<HTMLDivElement> = useCallback((e) => {
        e.preventDefault()
        e.stopPropagation()
    }, [])

    return useMemo(
        (): TableColumn[] => [
            {
                key: 'name',
                sortKey: hideSorting ? undefined : 'name',
                isSortHighlightHidden: true,
                head: (
                    <div className="CorrelationTable__item-head">
                        <span className="CorrelationTable__item-title">Name</span>
                    </div>
                ),
                headText: 'Keyword Name',
                Value: ({ index }) => {
                    const keywordId = data[index].id ?? 0
                    const isSelected = selectedKeywordsIds.includes(keywordId)
                    const isDisabled = hasMaxSelected && !isSelected

                    return (
                        <TableCellSelectKeyword
                            isDisabled={isDisabled}
                            keywordId={keywordId}
                            className={cn(
                                "CorrelationTable__item-name",
                                {
                                    "is-disabled": isDisabled
                                }
                            )}
                            tooltipId={`correlation-item-${keywordId}`}
                        >
                            <span
                                className="CorrelationTable__toggle-label"
                                data-testid="textCorrelationItem"
                                dangerouslySetInnerHTML={{
                                    __html: markMatch(data[index]?.name ?? '', trimmedSearch, true)
                                }}
                            />
                            {
                                !isSelected ? (
                                    <Tooltip
                                        id={`correlation-item-${keywordId}`}
                                        style={{ zIndex: 4 }}
                                        place={"bottom-start"}
                                    >
                                        {isDisabled ? "Max 25 items" : "Select for export"}
                                    </Tooltip>
                                ) : null
                            }
                        </TableCellSelectKeyword>
                    )
                },
                stickyConfig: {
                    Value: ({ index }) => {
                        return <span data-testid="textCorrelationItem">{data[index]?.name ?? ''}</span>
                    },
                    accentLevel: 2,
                },
                valueText: (index) => data[index]?.name ?? '',
                accentLevel: 1,
                lockAccent: true,
                style: {
                    cell: {
                        maxWidth: getRem(NAME_COL_WIDTH),
                        wordBreak: 'break-word',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'pre-wrap',
                        paddingLeft: getRem(16),
                    },
                },
            },
            {
                key: 'baseCount',
                sortKey: hideSorting ? undefined : 'baseCount',
                isSortHighlightHidden: true,
                head: 'Base',
                headText: 'Base Size',
                Value: ({ index }) => {
                    const value = data[index]?.baseCount
                    const formattedValue = typeof value === 'number' ? getFormattedThousand(data[index]?.baseCount) : '-'
                    return <>{formattedValue}</>
                },
                stickyConfig: {
                    accentLevel: 2,
                },
                valueText: (index) => data[index]?.baseCount?.toString() ?? '',
                accentLevel: 1,
                lockAccent: true,
                style: {
                    cell: {
                        maxWidth: getRem(NUM_COL_WIDTH),
                    },
                },
            },
            {
                key: 'raw',
                sortKey: hideSorting ? undefined : `metric${selectedCorrelatingMetric ?? 0}`,
                isSortHighlightHidden: true,
                head: (
                    <div className="CorrelationTable__chart-head">
                        <span className="CorrelationTable__sort-icon">%</span>
                        <div className="CorrelationTable__chart-headlines" onClick={preventClick}>
                            <div className="CorrelationTable__selected-headline">
                                <div
                                    className="CorrelationTable__circle"
                                    data-testid="blockCorrelationMainHeadlineCircle"
                                />
                                <span data-testid="textCorrelationMainHeadlineCircle">{selectedHeadline}</span>
                            </div>
                            {
                                selectedCorrelatingMetric ? (
                                    <div className="CorrelationTable__relating-headline">
                                        <div
                                            className="CorrelationTable__circle is-active"
                                            data-testid="blockCorrelationRelatingHeadlineCircle"
                                        />
                                        <span data-testid="textCorrelationRelatingHeadlineCircle">{selectedHeadline} and also {relatingHeadline}</span>
                                    </div>
                                ) : null
                            }
                        </div>
                    </div>
                ),
                headText: `${getStringContentFromReactNode(selectedHeadline)} and also ${relatingHeadline}`,
                Value: ({ index }) => {
                    const matchedMetric = data[index]?.metrics?.find((metric) => metric.id === selectedCorrelatingMetric)
                    const value = matchedMetric?.value ?? 0
                    const numValue = matchedMetric?.numValue ?? 0

                    return (
                        <HorizontalBar
                            value={numValue}
                            modifiers={["inline"]}
                            label={value}
                        />
                    )
                },
                stickyConfig: {
                    Value: ({ index }) => {
                        const matchedMetric = data[index]?.metrics?.find((metric) => metric.id === selectedCorrelatingMetric)
                        const value = matchedMetric?.numValue ?? 0
                        return (
                            <HorizontalBar
                                value={value}
                                modifiers={["inline"]}
                                className="CorrelationTable__sticky-bar"
                                label={(
                                    <span className="CorrelationTable__sticky-bar-label">
										100%
									</span>
                                )}
                            />
                        )
                    },
                    accentLevel: 2,
                },
                valueText: (index) => {
                    const matchedMetric = data[index]?.metrics?.find((metric) => metric.id === selectedCorrelatingMetric)
                    const value = matchedMetric?.numValue ?? 0

                    return value.toString()
                },
                accentLevel: 1,
                lockAccent: true,
            },
        ],
        [
            data,
            trimmedSearch,
            selectedCorrelatingMetric,
            selectedKeywordsIds,
            hasMaxSelected,
            hideSorting,
            relatingHeadline,
            selectedHeadline,
            preventClick,
        ]
    );
}